import { z } from 'zod';
import { selectionOverridesSchema } from '../provider-selection/types';

// =============================================================================
// Shared Signal Types
// =============================================================================

export const referralSignalsSchema = z.object({
  cptCodes: z.array(z.string()).default([]),
  icdCodes: z.array(z.string()).default([]),
  patientType: z.enum(['new', 'existing']).nullable().default(null),
  isUrgent: z.boolean().default(false),
  genderCode: z.string().nullable().default(null),
  authExpiresAt: z.string().datetime({ offset: true }).nullable().default(null),
});

export type ReferralSignals = z.infer<typeof referralSignalsSchema>;

// =============================================================================
// Simulate
// =============================================================================

export const simulateRequestSchema = z.object({
  signals: referralSignalsSchema,
  providerId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  // Per-run algorithm tuning from the simulator. Merged over the org's saved
  // settings for THIS REQUEST ONLY — never persisted. Lets staff move a weight
  // and watch the ranking change without committing to it.
  overrides: selectionOverridesSchema.optional(),
});

export type SimulateRequest = z.infer<typeof simulateRequestSchema>;

// =============================================================================
// Simulate for a referral task
//
// Unlike `simulate` (caller supplies hand-typed signals), this asks the
// server to derive the referral signals from a task's linked referral
// document and run the matcher. Used by the Tasks Overview to preview the
// appointment type that would be selected for a real referral.
// =============================================================================

export const simulateForTaskRequestSchema = z.object({
  taskId: z.string().uuid(),
});

export type SimulateForTaskRequest = z.infer<typeof simulateForTaskRequestSchema>;

// =============================================================================
// Batch provider ranking for a page of referral tasks
//
// The Referrals worklist shows the ranked provider pool per row. Running
// `simulate-for-task` per row would re-read the whole roster, the eligibility
// catalog and the month's booking/slot counts once PER ROW, and would also run
// the AI tie-break for every tied row — on a list page. This asks for the one
// thing the column needs, once, for the whole page.
// =============================================================================

/** Bounded so a caller can't turn one request into an unbounded fan-out. */
export const SIMULATE_BATCH_MAX_TASKS = 50;

export const simulateBatchRequestSchema = z.object({
  taskIds: z.array(z.string().uuid()).min(1).max(SIMULATE_BATCH_MAX_TASKS),
});

export type SimulateBatchRequest = z.infer<typeof simulateBatchRequestSchema>;

/**
 * A ranked provider trimmed to what a list cell renders. Deliberately NOT the
 * full `RankedProvider`: the contribution breakdown is per-provider explanation
 * UI, and shipping it for every provider of every row on the page is payload
 * spent on something no cell displays.
 */
export const rankedProviderSummarySchema = z.object({
  rank: z.number(),
  providerId: z.string(),
  providerName: z.string(),
  /** Coarse match bucket, 1 = best. Lets a cell mark a weaker-tier suggestion. */
  tier: z.number(),
  codeMatchCount: z.number(),
  isDefaultFallback: z.boolean(),
  /**
   * The type this provider would be booked as — the SAME provider-scoped winner
   * the scheduling drawer resolves — so an unbooked row can show the type
   * scheduling has already determined, not a blank cell. Null when this provider
   * is tied across two enabled types (no single type to assert).
   */
  appointmentTypeName: z.string().nullable(),
});

export type RankedProviderSummary = z.infer<typeof rankedProviderSummarySchema>;

export const batchProviderRankingSchema = z.object({
  taskId: z.string(),
  /** False when the task has no resolvable referral document yet. */
  hasDocument: z.boolean(),
  /** Best first. Empty when nothing matched and no default provider is set. */
  providers: z.array(rankedProviderSummarySchema),
});

export type BatchProviderRanking = z.infer<typeof batchProviderRankingSchema>;

export const simulateBatchResponseSchema = z.object({
  results: z.array(batchProviderRankingSchema),
});

export type SimulateBatchResponse = z.infer<typeof simulateBatchResponseSchema>;

export const scoreContributionSchema = z.object({
  signal: z.string(),
  points: z.number(),
  detail: z.string().optional(),
});

export type ScoreContribution = z.infer<typeof scoreContributionSchema>;

export const simulatedTypeResultSchema = z.object({
  appointmentTypeId: z.string(),
  appointmentTypeName: z.string(),
  score: z.number(),
  contributions: z.array(scoreContributionSchema),
  eliminated: z.boolean(),
  eliminationReason: z.string().optional(),
});

export type SimulatedTypeResult = z.infer<typeof simulatedTypeResultSchema>;

// =============================================================================
// Ranked providers
//
// The same scored rows as `allTypes`, regrouped by PROVIDER instead of by type.
// `appointment_type_eligibility` is a (provider, location, type) triple, so the
// scorer already produces one row per provider — this surfaces that dimension
// instead of collapsing it.
//
// A parallel array rather than fields on `allTypes`: the type view and its
// existing consumers keep working untouched.
// =============================================================================

export const rankedProviderSchema = z.object({
  /** 1-based position in the offer order. */
  rank: z.number(),
  providerId: z.string(),
  providerName: z.string(),
  locationId: z.string(),
  locationName: z.string(),
  /**
   * The type this provider would be booked as, resolved by the SAME logic the
   * scheduling drawer uses (provider-scoped winner/tie at the ranked location).
   * Null when this provider is genuinely tied across two enabled types — see
   * `isTie` / `tiedTypes` — so the pool never asserts an arbitrary type the way
   * the old "best row" pick did.
   */
  appointmentTypeId: z.string().nullable(),
  appointmentTypeName: z.string().nullable(),
  matchScore: z.number(),
  /** Distinct CPT + ICD codes matched. Drives tier, so it drives hard precedence. */
  codeMatchCount: z.number(),
  /**
   * Coarse match bucket, 1 = best. Ordering is tier-first: priority strategies
   * only reorder providers WITHIN a tier, never across one — so a lighter load
   * can never outrank a stronger clinical code match.
   */
  tier: z.number(),
  contributions: z.array(scoreContributionSchema),
  /**
   * True when this provider is equally enabled for two or more types that scored
   * the same — the type is a real question for a human, so `appointmentTypeId`
   * is null and `tiedTypes` lists the candidates for inline resolution.
   */
  isTie: z.boolean().default(false),
  /**
   * The tied candidate types when `isTie`, each with its own score breakdown so
   * the row can render the reasons and the "select this type" flow. Empty when
   * the type resolved cleanly.
   */
  tiedTypes: z
    .array(
      z.object({
        appointmentTypeId: z.string(),
        appointmentTypeName: z.string(),
        contributions: z.array(scoreContributionSchema),
      })
    )
    .default([]),
  /** True when this provider came from the org's default-provider fallback, not a match. */
  isDefaultFallback: z.boolean(),
  // Normalized 0..1 within the pool. Null when the strategy isn't enabled.
  fairnessScore: z.number().nullable(),
  weightScore: z.number().nullable(),
  /** Weighted sum of the enabled strategies. Null when none are enabled. */
  compositeScore: z.number().nullable(),
});

export type RankedProvider = z.infer<typeof rankedProviderSchema>;

export const appliedPreferenceSchema = z.object({
  preferenceId: z.string(),
  reason: z.string(),
  aiReasoning: z.string(),
});

export type AppliedPreference = z.infer<typeof appliedPreferenceSchema>;

export const simulationExplanationSchema = z.object({
  allTypes: z.array(simulatedTypeResultSchema),
  // Ranked provider pool for this referral, best first. Empty when no provider
  // is eligible and no default fallback is configured.
  providers: z.array(rankedProviderSchema).default([]),
  fallbackPassUsed: z.number(),
  appliedPreference: appliedPreferenceSchema.nullable(),
});

export type SimulationExplanation = z.infer<typeof simulationExplanationSchema>;

export const appointmentTypeRecommendationSchema = z.object({
  appointmentTypeId: z.string().nullable(),
  appointmentTypeName: z.string().nullable(),
  confidence: z.enum(['high', 'medium', 'low', 'none']),
  isTie: z.boolean(),
  tiedTypeIds: z.array(z.string()),
  explanation: simulationExplanationSchema,
});

export type AppointmentTypeRecommendation = z.infer<typeof appointmentTypeRecommendationSchema>;

export const simulateForTaskResponseSchema = z.object({
  // `false` when the task has no resolvable referral document yet (manual
  // task, extraction not finished, or non-referral source). The FE renders
  // a quiet "no referral document" state rather than a "no match" error.
  hasDocument: z.boolean(),
  // The signals derived from the referral document — surfaced so the UI can
  // show WHAT the match was run against (codes, urgency, patient type).
  signals: referralSignalsSchema,
  recommendation: appointmentTypeRecommendationSchema,
});

export type SimulateForTaskResponse = z.infer<typeof simulateForTaskResponseSchema>;

// =============================================================================
// Preferences
// =============================================================================

export const appointmentTypePreferenceResponseSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  competingTypeIds: z.array(z.string()),
  situationCptCodes: z.array(z.string()),
  situationIcdCodes: z.array(z.string()),
  situationPatientType: z.string().nullable(),
  situationProviderId: z.string().nullable(),
  situationLocationId: z.string().nullable(),
  chosenTypeId: z.string(),
  reason: z.string(),
  applyCount: z.number(),
  lastAppliedAt: z.string().nullable(),
  decidedBy: z.string(),
  decidedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AppointmentTypePreferenceResponse = z.infer<
  typeof appointmentTypePreferenceResponseSchema
>;

export const recordPreferenceRequestSchema = z
  .object({
    competingTypeIds: z.array(z.string().uuid()).min(2),
    chosenTypeId: z.string().uuid(),
    reason: z.string().min(1),
    signals: referralSignalsSchema,
    providerId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
  })
  .refine((d) => d.competingTypeIds.includes(d.chosenTypeId), {
    message: 'chosenTypeId must be one of competingTypeIds',
    path: ['chosenTypeId'],
  });

export type RecordPreferenceRequest = z.infer<typeof recordPreferenceRequestSchema>;
